import { Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { DropdownSelect } from "components/DropdownSelect";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  ButtonsContainer,
  FormContainer,
  InputContainer,
} from "./style";
import { useEmpresas } from "hooks/useEmpresas";
import { useEffect, useState } from "react";
import { gerenciarEmplacamentoFiltroPR } from "../../validate/validator";
import { IFiltroPR } from "../../Interfaces/IFiltroPR";
import { useDivisoes } from "hooks/useDivisoes";
import { EtapaGerenciarEmplacamentosEnum } from "../../enums/EtapaGerenciarEmplacamentos.enum";
import { Required } from "components/Required";
import { mensagensErroGerenciarEmplacamentos } from "../Constants/mensagensErroGerenciarEmplacamentos";
import { CustomInput } from "components/CustomInput";

interface IFiltrosProps {
  submitForm: (e: IFiltroPR) => void;
}

export function FiltrosPR({ submitForm }: IFiltrosProps) {
  const { register, handleSubmit, reset, control, setValue } = useForm({
    resolver: yupResolver(gerenciarEmplacamentoFiltroPR),
  });

  const [idEmpresaEscolhida, setIdEmpresaEscolhida] = useState<number>(0);
  const [desabilitarDivisao, setDesabilitarDivisao] = useState<boolean>(true);
  const [desabilitarStatus, setDesabilitarStatus] = useState<boolean>(true);
  const [desabilitarBuscar, setDesabilitarBuscar] = useState<boolean>(true);
  const [algumStatusEscolhido, setAlgumStatusEscolhido] =
    useState<boolean>(false);
  const [algumaEtapaEscolhida, setAlgumaEtapaEscolhida] =
    useState<boolean>(false);
  const [algumaDivisaoEscolhida, setAlgumaDivisaoEscolhida] =
    useState<boolean>(false);

  const onChangeEmpresaEscolhida = (empresa: number | null) => {
    setValue("etapa", "");
    setValue("statusEtapa", "");
    setValue("divisao", 0);
    setValue("informeFaturamento", "");
    setValue("mensagemErro", "");
    setValue("processoChassi", "");
    setAlgumaEtapaEscolhida(false);
    setAlgumStatusEscolhido(false);
    setAlgumaDivisaoEscolhida(false);

    if (empresa) {
      setIdEmpresaEscolhida(empresa);
      setDesabilitarDivisao(false);
      return;
    }

    setIdEmpresaEscolhida(0);
    setDesabilitarDivisao(true);
  };

  const { empresasOptions } = useEmpresas();
  const { divisoesOptions } = useDivisoes(idEmpresaEscolhida);
  const onChangeEtapa = (etapa: string) => {
    setValue("statusEtapa", "");
    setAlgumStatusEscolhido(false);

    if (etapa) {
      setDesabilitarStatus(false);
      setAlgumaEtapaEscolhida(true);
      return;
    }

    setAlgumaEtapaEscolhida(false);
    setDesabilitarStatus(true);
  };

  const onChangeStatus = (status: string) => {
    if (status) {
      setAlgumStatusEscolhido(true);
      return;
    }

    setAlgumStatusEscolhido(false);
  };

  useEffect(() => {
    if (algumaDivisaoEscolhida) {
      if (algumaEtapaEscolhida && !algumStatusEscolhido) {
        setDesabilitarBuscar(true);
        return;
      }

      setDesabilitarBuscar(false);
      return;
    }

    setDesabilitarBuscar(true);
  }, [algumStatusEscolhido, algumaDivisaoEscolhida, algumaEtapaEscolhida]);

  return (
    <FormContainer>
      <Form
        noValidate
        onSubmit={handleSubmit((dados: IFiltroPR) => submitForm(dados))}
      >
        <Row className="mb-4">
          <InputContainer>
            <Form.Group>
              <Form.Label>
                Empresa <Required />
              </Form.Label>
              <Controller
                control={control}
                name="empresa"
                render={({ field: { onChange, value } }) => (
                  <DropdownSelect
                    options={empresasOptions}
                    title=""
                    selectedValue={value}
                    onSelect={(value) => {
                      onChange(value);
                      setDesabilitarBuscar(true);
                      if (!value) {
                        onChangeEmpresaEscolhida(0);
                        return;
                      }
                      onChangeEmpresaEscolhida(value);
                    }}
                    searchable
                    required
                  />
                )}
              />
            </Form.Group>
          </InputContainer>
          <InputContainer>
            <Form.Group>
              <Form.Label>
                Divisão <Required />
              </Form.Label>
              <Controller
                control={control}
                name="divisao"
                render={({ field: { onChange, value } }) => (
                  <DropdownSelect
                    options={divisoesOptions}
                    title=""
                    selectedValue={value}
                    onSelect={(value) => {
                      onChange(value);
                      if (value) {
                        setAlgumaDivisaoEscolhida(true);
                        return;
                      }
                      setAlgumaDivisaoEscolhida(false);
                    }}
                    searchable
                    disabled={desabilitarDivisao}
                    gapDropdownContainer="0"
                    required
                  />
                )}
              />
            </Form.Group>
          </InputContainer>
          <InputContainer>
            <Form.Group>
              <Form.Label>Etapa</Form.Label>
              <Controller
                control={control}
                name="etapa"
                render={({ field: { onChange, value } }) => (
                  <DropdownSelect
                    options={[
                      {
                        value: EtapaGerenciarEmplacamentosEnum.ESCOLHER_PLACA,
                        label: "Escolher placa",
                      },
                      {
                        value: EtapaGerenciarEmplacamentosEnum.GERAR_PROCESSO,
                        label: "Gerar processo",
                      },
                      {
                        value: EtapaGerenciarEmplacamentosEnum.PAGAR_TAXA,
                        label: "Pagar taxa",
                      },
                      {
                        value:
                          EtapaGerenciarEmplacamentosEnum.GERAR_PROCESSO_DIGITAL,
                        label: "Gerar Processo Digital",
                      },
                      {
                        value:
                          EtapaGerenciarEmplacamentosEnum.APROPRIAR_PROCESSO,
                        label: "Apropriar processo",
                      },
                      {
                        value:
                          EtapaGerenciarEmplacamentosEnum.EMITIR_LICENCIAMENTO,
                        label: "Emitir licenciamento",
                      },
                      {
                        value: EtapaGerenciarEmplacamentosEnum.SOLICITAR_PLACA,
                        label: "Solicitar placa",
                      },
                      {
                        value: EtapaGerenciarEmplacamentosEnum.PAGAR_IPVA,
                        label: "Pagar IPVA",
                      },
                    ]}
                    title=""
                    selectedValue={value}
                    onSelect={(value) => {
                      onChange(value);
                      onChangeEtapa(value);
                    }}
                  />
                )}
              />
            </Form.Group>
          </InputContainer>
          <InputContainer>
            <Form.Group>
              <Form.Label>
                Status {!desabilitarStatus && <Required />}
              </Form.Label>
              <Controller
                control={control}
                name="statusEtapa"
                render={({ field: { onChange, value } }) => (
                  <DropdownSelect
                    options={[
                      { value: "finalizado", label: "Finalizado" },
                      { value: "pendente", label: "Pendente" },
                      { value: "processando", label: "Processando" },
                      { value: "erro", label: "Erro" },
                    ]}
                    title=""
                    selectedValue={value}
                    onSelect={(value) => {
                      onChange(value);
                      onChangeStatus(value);
                    }}
                    disabled={desabilitarStatus}
                  />
                )}
              />
            </Form.Group>
          </InputContainer>
        </Row>
        <Row>
          <InputContainer>
            <Form.Group>
              <Form.Label>Faturamento</Form.Label>
              <Controller
                control={control}
                name="informeFaturamento"
                render={({ field: { onChange, value } }) => (
                  <DropdownSelect
                    options={[
                      {
                        value: "enviado",
                        label: "Informe enviado",
                      },
                      {
                        value: "naoEnviado",
                        label: "Informe não enviado",
                      },
                    ]}
                    title=""
                    selectedValue={value}
                    onSelect={onChange}
                  />
                )}
              />
            </Form.Group>
          </InputContainer>
          <InputContainer>
            <Form.Group>
              <Form.Label>Observação</Form.Label>
              <Controller
                control={control}
                name="mensagemErro"
                render={({ field: { onChange, value } }) => (
                  <DropdownSelect
                    options={mensagensErroGerenciarEmplacamentos.map(
                      (mensagem) => {
                        return { value: mensagem, label: mensagem };
                      }
                    )}
                    title=""
                    selectedValue={value}
                    onSelect={onChange}
                  />
                )}
              />
            </Form.Group>
          </InputContainer>
          <InputContainer>
            <Form.Group>
              <Form.Label>Processo / Chassi</Form.Label>
              <Controller
                control={control}
                name="processoChassi"
                render={({ field: { onChange } }) => (
                  <CustomInput
                    type="text"
                    placeholder="Busque por processo ou chassi"
                    style={{ height: "2.2rem" }}
                    onChange={onChange}
                  />
                )}
              />
            </Form.Group>
          </InputContainer>
          <InputContainer>
            <Form.Group>
              <ButtonsContainer>
                <Button type="submit" disabled={desabilitarBuscar}>
                  Buscar
                </Button>
              </ButtonsContainer>
            </Form.Group>
          </InputContainer>
        </Row>
      </Form>
    </FormContainer>
  );
}
